/**
 * Reusable loading spinner component
 * Replaces inline <div className="spinner"></div> throughout the app
 * 
 * Usage:
 *   <LoadingSpinner /> - Standard size, centered
 *   <LoadingSpinner size="small" /> - Small inline spinner
 *   <LoadingSpinner size="large" /> - Large page loader
 *   <LoadingSpinner text="Loading..." /> - With message
 */

interface LoadingSpinnerProps {
  size?: 'small' | 'medium' | 'large';
  text?: string;
  fullPage?: boolean;
}

export default function LoadingSpinner({ 
  size = 'medium', 
  text,
  fullPage = false 
}: LoadingSpinnerProps) {
  const sizeMap = {
    small: { width: '24px', height: '24px', borderWidth: '2px' },
    medium: { width: '40px', height: '40px', borderWidth: '3px' },
    large: { width: '60px', height: '60px', borderWidth: '4px' },
  };

  const spinnerStyle = {
    border: `${sizeMap[size].borderWidth} solid var(--color-border)`,
    borderTopColor: 'var(--color-accent)',
    borderRadius: '50%',
    width: sizeMap[size].width,
    height: sizeMap[size].height,
    animation: 'spin 0.8s linear infinite',
  };

  const containerStyle: React.CSSProperties = fullPage
    ? {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        gap: '1rem',
      }
    : {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1rem',
        padding: '2rem',
      };

  return (
    <div style={containerStyle}>
      <div style={spinnerStyle} />
      {text && (
        <p style={{ 
          color: 'var(--color-text)', 
          fontSize: size === 'small' ? '0.875rem' : '1rem',
          opacity: 0.7 
        }}>
          {text}
        </p>
      )}
    </div>
  );
}

